/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useState } from "react"
import { ImageOutlined } from "@duxton-web/icons"
import { Avatar, Button, Checkbox, Drawer } from "@duxton-web/ui"
import classNames from "classnames"

import {
  RETURN_REFUND_TEXT,
  ReturnRequestItem,
} from "ecommerce/constants/returnRefund"
import {
  getClarifyState,
  RETURN_CLARIFY_STATE,
} from "ecommerce/utils/returnRefundHelpers"

import styles from "./ChatItemPickerSheet.modules.css"

interface ChatItemPickerSheetProps {
  open: boolean
  /** Every item of the request — each row renders its own clarify state. */
  items: ReturnRequestItem[]
  onCancel(): void
  /**
   * The checked items, on the Chat CTA. One REQUEST_INFO batch — the server
   * opens (or reuses) the request's room and attaches all of them.
   */
  onChat(items: ReturnRequestItem[]): void
  /** An already-attached item's row — jump to its existing room, no submit. */
  onGoToChat(item: ReturnRequestItem): void
}

/**
 * The webview header chat entry's item picker: clarifying is the item's
 * once-per-item REQUEST_INFO decision, so before any chat opens the merchant
 * says which items the conversation is about.
 *
 * Row states are server-driven, mirroring the action sheet's clarify row:
 * an item already attached to the room offers `Go to chat`; one still
 * offering REQUEST_INFO in `eligibleResolutions` is selectable; anything
 * else renders greyed with no control — visible for context, not actionable.
 *
 * Webview-only by design: desktop keeps the chat-unavailable modal, because
 * spending the formal info request where no chat can follow strands the item.
 */
export const ChatItemPickerSheet: FC<ChatItemPickerSheetProps> = ({
  open,
  items,
  onCancel,
  onChat,
  onGoToChat,
}) => {
  const [checkedIDs, setCheckedIDs] = useState<string[]>([])

  /*
   * A fresh open starts unchecked. State survives a close by itself, so a
   * reopened sheet would otherwise resurrect a selection the merchant already
   * walked away from — and items clarified in between must not stay checked.
   */
  useEffect(() => {
    if (open) {
      setCheckedIDs([])
    }
  }, [open])

  const toggle = (itemID: string) =>
    setCheckedIDs(current =>
      current.includes(itemID)
        ? current.filter(id => id !== itemID)
        : [...current, itemID],
    )

  /*
   * Revalidated against the CURRENT clarify state, not just the checked ids: a
   * detail refresh can attach or retire a checked item while the sheet stays
   * open, and a stale id must neither keep the CTA enabled nor reach the
   * submit — the server would refuse the whole batch.
   */
  const checkedItems = items.filter(
    item =>
      checkedIDs.includes(item.id) &&
      getClarifyState(item) === RETURN_CLARIFY_STATE.available,
  )

  return (
    <Drawer
      // Own header, like the feature's other sheets: the Drawer default
      // upper-cases the title and adds a close icon — neither matches.
      title={null}
      closable={false}
      placement="bottom"
      height="auto"
      open={open}
      onClose={onCancel}
      destroyOnClose
      className={styles.sheet}
      /*
       * `body` zeroes Duxton's own `paddingLG`, which would otherwise stack with
       * `.content`'s inset; `wrapper` is a STYLE because Duxton forwards no
       * `classNames.wrapper`, and it carries the drop shadow that would render
       * square behind the rounded content — both per `OptionPickerSheet`.
       */
      styles={{
        body: { padding: 0 },
        wrapper: { borderRadius: "16px 16px 0 0" },
      }}
      // No visible chrome of its own, so the heading is the dialog's name.
      aria-label={RETURN_REFUND_TEXT.chatPickerTitle}
    >
      <div className={styles.content}>
        <h2 className={styles.title}>{RETURN_REFUND_TEXT.chatPickerTitle}</h2>
        {items.map(item => {
          // One shared rule with the action sheet's clarify row — see the helper.
          const clarifyState = getClarifyState(item)
          const isSelectable = clarifyState === RETURN_CLARIFY_STATE.available
          const isChecked = checkedIDs.includes(item.id)

          /*
           * The whole row is the checkbox's hit target, but never a label of
           * our own around it: Duxton `Checkbox` (like `Radio`) renders its own
           * `<label>`, and nesting one label inside another is invalid HTML that
           * browsers and screen readers disagree on. So — the OptionPickerSheet
           * pattern — this class lands on the Checkbox's OWN label, with the row
           * content as its children. Non-selectable rows render as plain divs.
           */
          const rowContent = (
            <>
              <Avatar
                shape="square"
                src={item.imageURL}
                alt={item.name}
                // Without a fallback, a missing imageURL paints an empty box.
                icon={<ImageOutlined />}
                className={styles.thumbnail}
              />
              <span className={styles.rowText}>
                <span className={styles.rowName}>{item.name}</span>
                {/* Price-led line: without a price, `× 1` would dangle alone. */}
                {item.price && (
                  <span className={styles.rowPrice}>
                    {item.price} × {item.quantity}
                  </span>
                )}
              </span>
            </>
          )

          if (isSelectable) {
            return (
              <Checkbox
                key={item.id}
                className={classNames(styles.row, styles.checkRow, {
                  [styles.rowChecked]: isChecked,
                })}
                checked={isChecked}
                onChange={() => toggle(item.id)}
              >
                {rowContent}
              </Checkbox>
            )
          }

          return (
            <div
              key={item.id}
              className={classNames(styles.row, styles.rowInert)}
            >
              {rowContent}
              {clarifyState === RETURN_CLARIFY_STATE.chat && (
                <Button
                  shape="round"
                  type="secondary"
                  size="small"
                  className={styles.goToChat}
                  onClick={() => onGoToChat(item)}
                >
                  {RETURN_REFUND_TEXT.goToChatCta}
                </Button>
              )}
            </div>
          )
        })}
        <Button
          block
          shape="round"
          type="primary"
          className={styles.chatCta}
          disabled={checkedItems.length === 0}
          onClick={() => onChat(checkedItems)}
        >
          {RETURN_REFUND_TEXT.chatPickerCta}
        </Button>
      </div>
    </Drawer>
  )
}
